import React, { useCallback, useEffect, useRef, useState } from 'react';
import Modal from './Modal';
import './ConfirmDialog.css';

/**
 * Reusable confirm dialog used for destructive (and the occasional
 * neutral) operations across the app. Composes the existing `<Modal>`
 * (which already handles backdrop dismissal, Escape, fullscreen on
 * mobile, focus management) and layers a 2-button footer on top.
 *
 * The confirm button can be awaited: pass an async `onConfirm` and the
 * dialog will flip into a `busy` state for the duration so the parent
 * can `await` the destructive call before closing.
 *
 * Default UX:
 *   - Auto-focus the confirm button on open (modal already focuses the
 *     first focusable element; the layout places confirm last so we
 *     re-focus it explicitly after mount to honour the spec).
 *   - Escape → cancel (inherited from Modal).
 *   - Click outside → cancel (inherited from Modal, gated to false
 *     while `busy` so a mid-flight delete can't be dropped).
 *   - `variant: 'danger'` paints the confirm red and applies a
 *     `is-danger` accent line at the top of the panel.
 */

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: React.ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: 'danger' | 'primary';
  /** External busy flag; merged with the internal flag that's set
   *  while an async `onConfirm` is in flight. */
  busy?: boolean;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

const SpinnerIcon: React.FC = () => (
  <span className="confirm-dialog-spinner" aria-hidden />
);

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  title,
  message,
  confirmLabel = 'Confirmă',
  cancelLabel = 'Anulează',
  variant = 'primary',
  busy: busyExternal,
  onConfirm,
  onCancel,
}) => {
  const [busyInternal, setBusyInternal] = useState(false);
  const busy = !!busyExternal || busyInternal;
  const confirmBtnRef = useRef<HTMLButtonElement>(null);

  // Reset the internal busy flag whenever the dialog opens/closes so a
  // dialog reopened after a previous close doesn't inherit a stale
  // spinner state.
  useEffect(() => {
    if (!open) setBusyInternal(false);
  }, [open]);

  // Auto-focus the confirm button on open. We wait one frame past
  // Modal's own focus pass so the focus lands on confirm rather than
  // cancel (which would happen to be the first focusable element if we
  // didn't intervene).
  useEffect(() => {
    if (!open) return;
    const t = window.setTimeout(() => {
      confirmBtnRef.current?.focus();
    }, 16);
    return () => window.clearTimeout(t);
  }, [open]);

  const handleConfirm = useCallback(async () => {
    if (busy) return;
    const result = onConfirm();
    if (result && typeof (result as Promise<unknown>).then === 'function') {
      setBusyInternal(true);
      try {
        await result;
      } finally {
        setBusyInternal(false);
      }
    }
  }, [busy, onConfirm]);

  const handleCancel = useCallback(() => {
    if (busy) return;
    onCancel();
  }, [busy, onCancel]);

  const confirmClass = `btn ${variant === 'danger' ? 'btn-danger' : 'btn-primary'} confirm-dialog-confirm`;

  const footer = (
    <div className="confirm-dialog-actions">
      <button
        type="button"
        className="btn btn-secondary"
        onClick={handleCancel}
        disabled={busy}
      >
        {cancelLabel}
      </button>
      <button
        ref={confirmBtnRef}
        type="button"
        className={confirmClass}
        onClick={handleConfirm}
        disabled={busy}
        aria-busy={busy || undefined}
      >
        {busy ? (
          <>
            <SpinnerIcon />
            <span>{confirmLabel}</span>
          </>
        ) : (
          confirmLabel
        )}
      </button>
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={title}
      footer={footer}
      closeOnBackdrop={!busy}
      closeOnEscape={!busy}
    >
      <div className={`confirm-dialog-body confirm-dialog-body--${variant}`}>
        {typeof message === 'string' ? (
          <p className="confirm-dialog-message">{message}</p>
        ) : (
          message
        )}
      </div>
    </Modal>
  );
};

export default ConfirmDialog;
